import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import type { AdornisTiptapExtension } from '@adornis/tiptap/client/extensions/ExtensionBase';
import { mergeAttributes } from '@tiptap/core';
import TiptapLink from '@tiptap/extension-link';

export const LinkTranslations = {
  link: {
    tooltip: {
      linkAdd: ['Link einfügen', 'Add link'],
      linkRemove: ['Link entfernen', 'Remove Link'],
    },
  },
};

export const Link: AdornisTiptapExtension = {
  buttons: ({ editor, host }) => {
    const buttons = [
      {
        name: 'insertLink',
        icon: 'link',
        action: async () => {
          const existingLink = editor.getAttributes('link');

          if (existingLink?.href) {
            const editedLink = await DTiptapPromptLink.showPopup<PromptTiptapExtensionLink>({
              props: {
                href: existingLink.href,
                openInNewTab: existingLink?.target === '_blank',
                color: existingLink.color,
              },
            });
            if (!editedLink) return;
            editor
              .chain()
              .extendMarkRange('link')
              .updateAttributes('link', { href: editedLink.href, target: editedLink.target, color: editedLink.color })
              .run();
            return;
          }

          const link = await DTiptapPromptLink.showPopup<PromptTiptapExtensionLink>();
          if (link?.href === null || link?.href === undefined) return;
          editor.commands.setLink({ ...link });
        },
        tooltip: host.translator.translate('tiptap', 'link', 'tooltip', 'linkAdd'),
      },
    ];
    if (editor.getAttributes('link').href)
      buttons.push({
        name: 'removeLink',
        icon: 'link_off',
        action: async () => {
          editor.chain().focus().unsetLink().run();
        },
        tooltip: host.translator.translate('tiptap', 'link', 'tooltip', 'linkRemove'),
      });
    return buttons;
  },
  extensions: [
    TiptapLink.extend({
      renderHTML({ HTMLAttributes }) {
        return [
          `${DesignSystem.prefix}-link`,
          mergeAttributes(this.options.HTMLAttributes, {
            ...HTMLAttributes,
            color: HTMLAttributes.color,
          }),
          0,
        ];
      },
      parseHTML() {
        return [{ tag: 'x-link' }, { tag: `${DesignSystem.prefix}-link` }];
      },
      addAttributes() {
        return {
          href: {
            default: null,
          },
          target: {
            default: this.options.HTMLAttributes.target,
          },
          color: {
            default: null,
          },
          icon: {
            default: 'link',
            renderHTML(attributes) {
              return {
                icon: 'link',
                style: 'text-decoration: underline',
              };
            },
          },
        };
      },
    }),
  ],
};

import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { xComponents } from '@adornis/chemistry/elements/x-components.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import '@adornis/fonts/fonts.js';
import '@adornis/forms/x-checkbox.js';
import '@adornis/forms/x-input.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export type PromptTiptapExtensionLink = Maybe<{ href: string; target: string; color: string }>;

/**
 * @element las-prompt-tiptap-extension-link
 */
@customElement('d-tiptap-prompt-link')
export class DTiptapPromptLink extends XNativeDialog<PromptTiptapExtensionLink> {
  static override get element_name(): string {
    return 'd-tiptap-prompt-link';
  }

  @property({ attribute: false }) href = '';
  @property({ attribute: false }) openInNewTab = true;
  @property({ attribute: false }) color = '';

  override content() {
    return xComponents(html`
      <x-flex
        ${css({
          padding: `${DesignSystem.currentTheme.getValue().spacing.sm} ${
            DesignSystem.currentTheme.getValue().spacing.lg
          }`,
          gap: '24px',
        })}
      >
        <x-h4> Erstelle einen Link </x-h4>

        <x-input
          .value=${this.href}
          placeholder=${'Link'}
          @value-picked=${(e: ValueEvent<string>) => {
            this.href = e.detail.value;
          }}
        ></x-input>
        <x-checkbox
          .value=${this.openInNewTab}
          .label=${'In neuem Fenster öffnen'}
          @value-picked=${(e: ValueEvent<boolean>) => {
            this.openInNewTab = e.detail.value;
          }}
        ></x-checkbox>
        <x-input
          clearable
          type="color"
          .value=${this.color}
          placeholder=${'Farbe'}
          @value-picked=${(e: ValueEvent<string>) => {
            this.color = e.detail.value;
          }}
        ></x-input>

        <x-flex horizontal center space="md">
          <x-button tone="neutral" mode="outline" @click=${() => this.cancel()}> Abbrechen </x-button>
          <x-button
            mode="action"
            @click=${() =>
              this.close({ href: this.href, target: this.openInNewTab ? '_blank' : '_self', color: this.color })}
          >
            Speichern
          </x-button>
        </x-flex>
      </x-flex>
    `);
  }

  override async cancel(returnVal?: any) {
    return this.close(null);
  }
}
